import { useEffect, useMemo, useState } from "react";
import { BriefcaseBusiness, ChevronDown, ChevronUp, Loader2, Pencil, Plus } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import type { PrepareApplicationEmployment } from "@/models/underwriting";
import { applicationService } from "@/services/ApplicationService";
import { toast } from "sonner";

const OCCUPATION_STATUSES = [
  "Employed Full Time",
  "Employed Part Time",
  "Self Employed",
  "Armed Forces",
  "Not Employed",
  "Retired",
  "Agency Worker",
  "Contract Worker",
  "Sub-contractor",
  "Disability",
  "Housewife",
  "Student",
  "Carer",
  "Carer - Non Prof",
].map((occupationStatusName, index) => ({ occupationStatusId: index + 1, occupationStatusName }));

const emptyEmployment = (): PrepareApplicationEmployment => ({
  occupationStatus: { occupationStatusId: 0, occupationStatusName: "" },
  yearsAtEmployment: 0,
  monthsAtEmployment: 0,
  occupation: "",
  employerName: "",
  employerPhoneNumber: "",
  employerAddress: {
    subBuilding: "",
    buildingNumber: "",
    buildingName: "",
    streetName: "",
    town: "",
    county: "",
    postcode: "",
  },
  isCurrentEmployment: false,
  addressId: 0,
  companyName: "",
});

function cloneEmployment(employment: PrepareApplicationEmployment): PrepareApplicationEmployment {
  return {
    ...employment,
    occupationStatus: { ...employment.occupationStatus },
    employerAddress: { ...emptyEmployment().employerAddress!, ...employment.employerAddress },
  };
}

function formatAddress(employment: PrepareApplicationEmployment): string {
  const address = employment.employerAddress;
  if (!address) return "Address not provided";
  return [
    [address.subBuilding, address.buildingNumber, address.buildingName].filter(Boolean).join(" "),
    address.streetName,
    address.town,
    address.county,
    address.postcode,
  ].filter(Boolean).join(", ") || "Address not provided";
}

function EmploymentItem({
  employment,
  onEdit,
  canEdit,
}: {
  employment: PrepareApplicationEmployment;
  onEdit: () => void;
  canEdit: boolean;
}) {
  return (
    <div className="grid gap-3 border-b border-border px-4 py-4 last:border-b-0 sm:grid-cols-[minmax(0,1.1fr)_minmax(0,1.2fr)_minmax(150px,.7fr)_auto]">
      <div>
        <p className="text-xs font-medium text-foreground">{employment.occupation || "Occupation not provided"}</p>
        <p className="mt-1 text-[11px] text-muted-foreground">{employment.employerName || "Employer not provided"}</p>
      </div>
      <p className="text-[11px] leading-5 text-muted-foreground">{formatAddress(employment)}</p>
      <div>
        <p className="text-[11px] text-foreground">{employment.occupationStatus?.occupationStatusName || "Status not provided"}</p>
        <p className="mt-1 text-[11px] text-muted-foreground">
          {employment.yearsAtEmployment} years, {employment.monthsAtEmployment} months
        </p>
      </div>
      {canEdit && (
        <Button variant="ghost" size="sm" className="h-8 gap-1.5 justify-self-start text-purple-700" onClick={onEdit}>
          <Pencil className="size-3.5" />
          Edit
        </Button>
      )}
    </div>
  );
}

interface EmploymentEditorProps {
  employment: PrepareApplicationEmployment;
  isEdit: boolean;
  saving: boolean;
  onCancel: () => void;
  onSave: (employment: PrepareApplicationEmployment) => void;
  onDelete?: () => void;
}

function EmploymentEditor({ employment, isEdit, saving, onCancel, onSave, onDelete }: EmploymentEditorProps) {
  const [draft, setDraft] = useState(() => cloneEmployment(employment));
  const [errors, setErrors] = useState<Record<string, string>>({});

  const labelClassName = "text-[12px] font-semibold text-foreground";
  const inputClassName = "mt-1 h-9 text-[12px] text-foreground";
  const selectClassName = "mt-1 h-9 w-full rounded-md border border-input bg-transparent px-3 text-[12px] text-foreground outline-none focus:border-ring focus:ring-3 focus:ring-ring/50";

  useEffect(() => {
    setDraft(cloneEmployment(employment));
    setErrors({});
  }, [employment]);

  const setField = <K extends keyof PrepareApplicationEmployment>(key: K, value: PrepareApplicationEmployment[K]) => {
    setDraft((current) => ({ ...current, [key]: value }));
  };

  const setAddressField = (key: keyof NonNullable<PrepareApplicationEmployment["employerAddress"]>, value: string) => {
    setDraft((current) => ({
      ...current,
      employerAddress: { ...emptyEmployment().employerAddress!, ...current.employerAddress, [key]: value },
    }));
  };

  const handleSave = () => {
    const nextErrors: Record<string, string> = {};
    if (!draft.occupationStatus.occupationStatusId) nextErrors.occupationStatus = "Occupation status is required";
    if (draft.yearsAtEmployment === 0 && draft.monthsAtEmployment === 0) nextErrors.employmentTime = "Employment duration is required";
    if (!Number.isInteger(draft.monthsAtEmployment) || draft.monthsAtEmployment < 0 || draft.monthsAtEmployment > 11) nextErrors.monthsAtEmployment = "Months must be a whole number from 0 to 11";
    if (!draft.occupation.trim()) nextErrors.occupation = "Occupation is required";
    if (!draft.employerName?.trim()) nextErrors.employerName = "Employer name is required";
    if (!draft.employerAddress?.streetName?.trim()) nextErrors.streetName = "Street name is required";
    if (!draft.employerAddress?.town?.trim()) nextErrors.town = "Town is required";
    setErrors(nextErrors);
    if (Object.keys(nextErrors).length === 0) onSave(draft);
  };

  const fieldError = (key: string) => errors[key] ? <p className="mt-1 text-[11px] text-rose-600">{errors[key]}</p> : null;

  return (
    <div className="px-4 py-4">
      <div className="mb-4">
        <h2 className="text-sm font-semibold text-foreground">{isEdit ? "Edit employment" : "Add employment"}</h2>
        <p className="mt-1 text-xs text-muted-foreground">Update the customer&apos;s employment and employer address.</p>
      </div>

      <div className="grid gap-4 sm:grid-cols-2">
          <div className="sm:col-span-2">
            <Label htmlFor="employment-status" className={labelClassName}>Occupation status</Label>
            <select
              id="employment-status"
              className={selectClassName}
              value={draft.occupationStatus.occupationStatusId}
              onChange={(event) => {
                const status = OCCUPATION_STATUSES.find((item) => item.occupationStatusId === Number(event.target.value));
                if (status) setField("occupationStatus", status);
              }}
              aria-invalid={Boolean(errors.occupationStatus)}
            >
              <option value={0}>Select a status</option>
              {OCCUPATION_STATUSES.map((status) => <option key={status.occupationStatusId} value={status.occupationStatusId}>{status.occupationStatusName}</option>)}
            </select>
            {fieldError("occupationStatus")}
          </div>

          <div>
            <Label htmlFor="employment-years" className={labelClassName}>Years</Label>
            <Input id="employment-years" className={inputClassName} type="number" min={0} max={99} value={draft.yearsAtEmployment} onChange={(event) => setField("yearsAtEmployment", Math.max(0, Number(event.target.value)))} />
            {fieldError("employmentTime")}
          </div>
          <div>
            <Label htmlFor="employment-months" className={labelClassName}>Months</Label>
            <Input id="employment-months" className={inputClassName} type="number" min={0} max={11} step={1} value={draft.monthsAtEmployment} onChange={(event) => setField("monthsAtEmployment", Number(event.target.value))} aria-invalid={Boolean(errors.monthsAtEmployment)} />
            {fieldError("monthsAtEmployment")}
          </div>
          <div className="sm:col-span-2">
            <Label htmlFor="employment-occupation" className={labelClassName}>Occupation</Label>
            <Input id="employment-occupation" className={inputClassName} value={draft.occupation} onChange={(event) => setField("occupation", event.target.value)} aria-invalid={Boolean(errors.occupation)} />
            {fieldError("occupation")}
          </div>
          <div>
            <Label htmlFor="employer-name" className={labelClassName}>Employer name</Label>
            <Input id="employer-name" className={inputClassName} value={draft.employerName ?? ""} onChange={(event) => setField("employerName", event.target.value)} aria-invalid={Boolean(errors.employerName)} />
            {fieldError("employerName")}
          </div>
          <div>
            <Label htmlFor="employer-phone" className={labelClassName}>Employer phone</Label>
            <Input id="employer-phone" className={inputClassName} value={draft.employerPhoneNumber ?? ""} onChange={(event) => setField("employerPhoneNumber", event.target.value)} />
          </div>

          <div className="border-t border-border pt-4 sm:col-span-2">
            <p className="text-[12px] font-semibold text-foreground">Employer address</p>
          </div>
          <div>
            <Label htmlFor="employer-building-number" className={labelClassName}>Building number</Label>
            <Input id="employer-building-number" className={inputClassName} value={draft.employerAddress?.buildingNumber ?? ""} onChange={(event) => setAddressField("buildingNumber", event.target.value)} />
          </div>
          <div>
            <Label htmlFor="employer-building-name" className={labelClassName}>Building name</Label>
            <Input id="employer-building-name" className={inputClassName} value={draft.employerAddress?.buildingName ?? ""} onChange={(event) => setAddressField("buildingName", event.target.value)} />
          </div>
          <div className="sm:col-span-2">
            <Label htmlFor="employer-street" className={labelClassName}>Street name</Label>
            <Input id="employer-street" className={inputClassName} value={draft.employerAddress?.streetName ?? ""} onChange={(event) => setAddressField("streetName", event.target.value)} aria-invalid={Boolean(errors.streetName)} />
            {fieldError("streetName")}
          </div>
          <div>
            <Label htmlFor="employer-town" className={labelClassName}>Town</Label>
            <Input id="employer-town" className={inputClassName} value={draft.employerAddress?.town ?? ""} onChange={(event) => setAddressField("town", event.target.value)} aria-invalid={Boolean(errors.town)} />
            {fieldError("town")}
          </div>
          <div>
            <Label htmlFor="employer-county" className={labelClassName}>County</Label>
            <Input id="employer-county" className={inputClassName} value={draft.employerAddress?.county ?? ""} onChange={(event) => setAddressField("county", event.target.value)} />
          </div>
          <div>
            <Label htmlFor="employer-postcode" className={labelClassName}>Postcode</Label>
            <Input id="employer-postcode" className={`${inputClassName} uppercase`} value={draft.employerAddress?.postcode ?? ""} onChange={(event) => setAddressField("postcode", event.target.value.toUpperCase())} />
          </div>
          <div className="flex items-end pb-2">
            <div className="flex items-center gap-2">
              <Checkbox id="current-employment" checked={draft.isCurrentEmployment} onCheckedChange={(checked) => setField("isCurrentEmployment", checked === true)} />
              <Label htmlFor="current-employment" className={labelClassName}>This is their current employment</Label>
            </div>
          </div>
      </div>

      <div className="mt-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>{isEdit && onDelete && <Button variant="destructive" disabled={saving} onClick={onDelete}>Delete employment</Button>}</div>
        <div className="flex justify-end gap-3">
          <Button variant="outline" disabled={saving} onClick={onCancel}>Cancel</Button>
          <Button className="bg-purple-600 text-white hover:bg-purple-700" disabled={saving} onClick={handleSave}>
            {saving && <Loader2 className="size-4 animate-spin" />}
            {isEdit ? "Save" : "Add employment"}
          </Button>
        </div>
      </div>
    </div>
  );
}

export interface EmploymentHistoryProps {
  applicationId: number;
  employmentHistory: PrepareApplicationEmployment[];
  canEdit: boolean;
  onChange: (employmentHistory: PrepareApplicationEmployment[]) => void;
}

export function EmploymentHistory({ applicationId, employmentHistory, canEdit, onChange }: EmploymentHistoryProps) {
  const [expanded, setExpanded] = useState(false);
  const [editorOpen, setEditorOpen] = useState(false);
  const [editingIndex, setEditingIndex] = useState<number | null>(null);
  const [saving, setSaving] = useState(false);
  const currentEmployment = useMemo(() => employmentHistory.find((employment) => employment.isCurrentEmployment) ?? null, [employmentHistory]);
  const previousEmployments = useMemo(() => employmentHistory.filter((employment) => !employment.isCurrentEmployment), [employmentHistory]);
  const activeEmployment = editingIndex == null ? emptyEmployment() : employmentHistory[editingIndex] ?? emptyEmployment();

  const openEditor = (index: number | null) => {
    setEditingIndex(index);
    setEditorOpen(true);
  };

  const persist = async (nextHistory: PrepareApplicationEmployment[], successMessage: string) => {
    setSaving(true);
    try {
      await applicationService.updateEmploymentHistory(applicationId, nextHistory);
      onChange(nextHistory);
      setEditorOpen(false);
      toast.success(successMessage);
    } catch {
      toast.error("Could not update employment history. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  const handleSave = (employment: PrepareApplicationEmployment) => {
    let nextHistory = employmentHistory.map(cloneEmployment);
    if (employment.isCurrentEmployment) {
      nextHistory = nextHistory.map((item) => ({ ...item, isCurrentEmployment: false }));
    }
    if (editingIndex == null) nextHistory.push(employment);
    else nextHistory[editingIndex] = employment;
    void persist(nextHistory, "Employment history updated successfully");
  };

  const handleDelete = () => {
    if (editingIndex == null || !window.confirm("Are you sure you want to delete this employment?")) return;
    void persist(employmentHistory.filter((_, index) => index !== editingIndex), "Employment deleted successfully");
  };

  const currentIndex = currentEmployment ? employmentHistory.indexOf(currentEmployment) : -1;

  return (
    <section className="rounded-xl border border-border bg-card shadow-sm">
      <div className="grid min-h-[54px] grid-cols-1 items-center gap-2 px-4 py-3 sm:grid-cols-[132px_minmax(0,1fr)_auto] sm:gap-3 sm:py-2">
        <div className="flex items-center gap-2 text-[11px] text-muted-foreground"><BriefcaseBusiness className="size-4" />Employment</div>
        <div className="text-xs text-foreground">
          {currentEmployment ? `${currentEmployment.occupationStatus.occupationStatusName}${currentEmployment.occupation ? ` (${currentEmployment.occupation})` : ""}` : "No current employment"}
        </div>
        <Button
          variant={expanded ? "default" : "outline"}
          size="sm"
          className={`h-8 justify-self-start gap-1.5 text-xs sm:justify-self-end ${expanded ? "bg-purple-600 text-white hover:bg-purple-700" : ""}`}
          onClick={() => setExpanded((value) => !value)}
        >
          Update employment
          {expanded ? <ChevronUp className="size-3.5" /> : <ChevronDown className="size-3.5" />}
        </Button>
      </div>

      {expanded && (
        <div className="border-t border-border">
          {editorOpen ? (
            <EmploymentEditor employment={activeEmployment} isEdit={editingIndex != null} saving={saving} onCancel={() => setEditorOpen(false)} onSave={handleSave} onDelete={editingIndex != null ? handleDelete : undefined} />
          ) : (
            <>
              <div className="flex items-center justify-between bg-muted/30 px-4 py-3">
                <h2 className="text-xs font-semibold text-foreground">Employment history</h2>
                {canEdit && <Button size="sm" className="h-8 gap-1.5 bg-purple-600 text-xs text-white hover:bg-purple-700" onClick={() => openEditor(null)}><Plus className="size-3.5" />Add employment</Button>}
              </div>
              {currentEmployment && (
                <div>
                  <p className="border-y border-border bg-muted/20 px-4 py-2 text-[11px] font-semibold text-foreground">Current employment</p>
                  <EmploymentItem employment={currentEmployment} canEdit={canEdit} onEdit={() => openEditor(currentIndex)} />
                </div>
              )}
              <div>
                <p className="border-y border-border bg-muted/20 px-4 py-2 text-[11px] font-semibold text-foreground">Previous employment</p>
                {previousEmployments.length > 0 ? previousEmployments.map((employment) => (
                  <EmploymentItem key={`${employment.employerName}-${employment.occupation}-${employmentHistory.indexOf(employment)}`} employment={employment} canEdit={canEdit} onEdit={() => openEditor(employmentHistory.indexOf(employment))} />
                )) : <p className="px-4 py-5 text-center text-[11px] text-muted-foreground">No previous employments found.</p>}
              </div>
            </>
          )}
        </div>
      )}
    </section>
  );
}